import { useQueryCore } from "@/hooks/useQueryCore";
import { localCache } from "@/lib/cache";
import {
  getFamily,
  getPeople,
  getChurch,
  getLastVisitByFamily,
  getVisits,
} from "@/lib/api";

export function useFamilyDetail(id) {
  const { data, loading, error, refetch } = useQueryCore({
    key: ["family-detail", id],
    enabled: !!id,
    queryFn: async () => {
      // Read existing compound before fetch — used as fallback for people on offline cache miss.
      const existingCompound = id ? await localCache.get(`fam_detail_compound_${id}`) : null;

      // family, people, and lastVisit all start in parallel.
      const familyPromise    = getFamily(id);
      const peoplePromise    = getPeople({ family_id: id });
      const lastVisitPromise = getLastVisitByFamily(id);

      // church and visits both need church_id from family — start them as
      // soon as familyPromise resolves instead of waiting for people/lastVisit.
      const churchPromise = familyPromise.then(
        (f) => (f?.church_id ? getChurch(f.church_id) : null)
      );
      const rawVisitsPromise = familyPromise.then((f) =>
        f?.church_id
          ? getVisits({ church_id: f.church_id, family_id: id, status: "realizada" })
          : []
      );

      const [family, people, lastVisit, church, rawVisits] = await Promise.all([
        familyPromise,
        peoplePromise,
        lastVisitPromise,
        churchPromise,
        rawVisitsPromise,
      ]);

      if (!family) {
        return { family: null, people: [], church: null, lastVisit: null, visits: [] };
      }

      // If getPeople had an offline cache miss, fall back to stale compound people
      // rather than writing people: [] and hiding members from the UI.
      const resolvedPeople = people.length > 0 ? people : (existingCompound?.people || []);

      const memberIds = new Set(resolvedPeople.map((p) => p.id));
      const visits = (rawVisits || [])
        .filter(
          (v) =>
            v.target_family_id === id ||
            (v.target_person_id && memberIds.has(v.target_person_id))
        )
        .sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));

      const compound = { family, people: resolvedPeople, church, lastVisit, visits };
      if (id && family) {
        localCache.set(`fam_detail_compound_${id}`, compound).catch(() => {});
        // Pre-warm PersonDetail Phase-0 compounds for every family member.
        // resps/lastVisit left empty — PersonDetail Phase 1 will fill them in
        // the background. The person, family, church, and sibling data render
        // instantly without waiting for those two extra network calls.
        (resolvedPeople || []).forEach(person => {
          localCache.set(`person_detail_${person.id}`, {
            person,
            family,
            church,
            familyMembers: resolvedPeople,
            resps: [],
            lastVisit: null,
          }).catch(() => {});
        });
      }
      return compound;
    },
    // Compound Dexie cache key — written explicitly above so cold-start always hits.
    cacheKey: id ? `fam_detail_compound_${id}` : null,
  });

  // Same resolved-guard pattern as usePersonDetail:
  // useQueryCore initialises data as []. Until the first fetch resolves to a plain
  // object, treat the unresolved [] state as loading so "not found" never flashes.
  const resolved = !Array.isArray(data);

  return {
    family:    data?.family    ?? null,
    people:    data?.people    ?? [],
    church:    data?.church    ?? null,
    lastVisit: data?.lastVisit ?? null,
    visits:    data?.visits    ?? [],
    loading:   loading || !resolved,
    error,
    refetch,
  };
}
